/**
 * RepairPhotoViewer — full-size photo dialog for the landlord Repair Detail Photos card
 * (RA-ATT-LANDLORD-READ). Built on the shared Radix dialog: focus is trapped inside and returned to
 * the thumbnail that opened it, Esc closes, and ← / → move between photos.
 */

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';

export interface RepairPhotoViewerItem {
  id: string;
  url: string;
  /** e.g. "Oct 5 · at submission" */
  caption: string;
}

const NAV_BUTTON_CLASS =
  'flex h-11 w-11 flex-none items-center justify-center rounded-full border border-input bg-card text-foreground hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring';

interface RepairPhotoViewerProps {
  items: RepairPhotoViewerItem[];
  /** Index of the open photo, or null when closed. */
  index: number | null;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  /** Called when the dialog has closed, to hand focus back to the thumbnail that opened it. */
  onReturnFocus: () => void;
}

export function RepairPhotoViewer({ items, index, onIndexChange, onClose, onReturnFocus }: RepairPhotoViewerProps) {
  const item = index === null ? undefined : items[index];
  const open = index !== null && item !== undefined;
  const label = open ? `Photo ${index + 1} of ${items.length}` : '';

  // Wraps around, so the focused prev/next button never becomes disabled (which would drop focus
  // out of the dialog and stop the arrow keys).
  const go = (next: number) => onIndexChange((next + items.length) % items.length);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (index === null) return;
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      go(index - 1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      go(index + 1);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      {open && item && (
        <DialogContent
          aria-modal="true"
          aria-label={label}
          aria-describedby={undefined}
          data-testid="repair-photo-viewer"
          onKeyDown={handleKeyDown}
          // Radix only restores focus to a <DialogTrigger>; the thumbnails are plain buttons.
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            onReturnFocus();
          }}
          className="max-w-4xl bg-card"
        >
          <DialogTitle className="pr-12 text-sm font-normal leading-normal text-foreground">
            {label} · {item.caption}
          </DialogTitle>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <button
              type="button"
              aria-label="Previous photo"
              data-testid="repair-photo-viewer-prev"
              className={`${NAV_BUTTON_CLASS} order-2 sm:order-1`}
              onClick={() => go(index - 1)}
            >
              <ChevronLeft className="h-5 w-5" aria-hidden="true" />
            </button>
            <div className="order-1 flex min-h-0 w-full items-center justify-center rounded-lg border border-border bg-muted sm:order-2 sm:w-auto sm:flex-1">
              <img
                src={item.url}
                alt={`Photo ${index + 1}`}
                className="max-h-[60vh] w-full object-contain"
              />
            </div>
            <button
              type="button"
              aria-label="Next photo"
              data-testid="repair-photo-viewer-next"
              className={`${NAV_BUTTON_CLASS} order-3`}
              onClick={() => go(index + 1)}
            >
              <ChevronRight className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
          <div className="flex items-center justify-end gap-3 sm:justify-between">
            <p className="m-0 hidden text-xs sm:block text-muted-foreground">Esc closes · ← → move between photos</p>
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="repair-photo-viewer-original"
              className="inline-flex min-h-[44px] flex-none items-center whitespace-nowrap rounded-lg border border-input px-3.5 text-sm text-foreground hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring"
            >
              Open original
            </a>
          </div>
        </DialogContent>
      )}
    </Dialog>
  );
}
